import React, { useEffect, useState } from 'react';
import AccessDenied from '../components/AccessDenied';
import LoadingState from '../components/LoadingState';
import { logout, useUser } from '../features/authentication';
import { getCatalogForSite } from '../features/catalog';
import WhiteLabel from '../filters/WhiteLabel';

const rolePermissions: Record<string, string> = {
  build: 'Build View',
  org: 'Account View',
  invite: 'Roles View',
  groups: 'Groups View',
  groupLeader: 'Group Leader',
  curriculum: 'Build Curriculum View',
  assign: 'Assign View',
} as const;

export const permissions: Record<string, string> = {
  ...rolePermissions,
  author: 'Author',
} as const;

type BaseRouteDefinition = {
  isPublic?: boolean;
  title: string;
};

type RouteWithSinglePermission = {
  requiredPermission: string | null;
  requireAnyPermission?: never;
};

type RouteWithAnyPermission = {
  requiredPermission?: never;
  requireAnyPermission: string[] | null;
};

type RouteWithNoPermissionRequirement = {
  requiredPermission?: undefined;
  requireAnyPermission?: undefined;
};

type RouteWithBuildTypePermission = { requiredPermissionByBuildType: true } & (
  | RouteWithSinglePermission
  | RouteWithAnyPermission
);

type RouteWithDirectPermission = { requiredPermissionByBuildType?: false | undefined } & (
  | RouteWithSinglePermission
  | RouteWithAnyPermission
  | RouteWithNoPermissionRequirement
);

type PublicRouteDefinition = {
  isPublic: true;
  requiredPermissionByBuildType?: false | undefined;
  requiredPermission?: undefined;
  requireAnyPermission?: undefined;
};

type NonPublicRouteDefinition = {
  isPublic?: false | undefined;
} & (RouteWithBuildTypePermission | RouteWithDirectPermission);

export type RouteDefinition = BaseRouteDefinition & (PublicRouteDefinition | NonPublicRouteDefinition);

type RouteProps = {
  match?: {
    params?: {
      buildType?: string;
    };
  };
};

type RouteGuardProps = {
  route: RouteDefinition;
  routeProps: RouteProps;
  children: React.ReactNode;
};

type AccessStatus = 'checking' | 'granted' | 'denied';

type Catalog = {
  _id: { id: string };
  shortCode: string;
};

const hasPermission = (userPermissions: string[] | undefined, permission: string | null): boolean => {
  return Boolean(permission && userPermissions?.includes(permission));
};

const hasAnyPermission = (userPermissions: string[] | undefined, requiredPermissions: string[]): boolean => {
  return requiredPermissions.some(permission => hasPermission(userPermissions, permission));
};

const getRequiredPermissions = (route: RouteDefinition, isAuthorBuildType: boolean): string[] => {
  if (route.requireAnyPermission?.length) {
    return route.requireAnyPermission;
  } else if (route.requiredPermissionByBuildType) {
    return [isAuthorBuildType ? permissions.author : permissions.build];
  } else if (route.requiredPermission) {
    return [route.requiredPermission];
  }
  return [];
};

export function RouteGuard({ route, routeProps, children }: RouteGuardProps) {
  const { user, isLoading } = useUser();
  const hasAuthenticatedUser = Boolean(user?.userId);
  const userPermissions = user?.permissions;
  const userAuthorCatalogs = user?.catalogs;
  const isPublicRoute = Boolean(route.isPublic);
  const [accessStatus, setAccessStatus] = useState<AccessStatus>('checking');

  const evaluateAuthorCatalogAccess = (isCancelled: () => boolean): void => {
    getCatalogForSite(WhiteLabel.catalog?.shortCode ?? '')
      .then(catalog => {
        if (isCancelled()) return;

        const catalogId = catalog._id.id;
        const userCanAuthorCatalog =
          Boolean(catalogId) && userAuthorCatalogs?.some(assignedCatalog => assignedCatalog._id.id === catalogId);

        setAccessStatus(userCanAuthorCatalog ? 'granted' : 'denied');
      })
      .catch(() => {
        if (isCancelled()) return;
        setAccessStatus('denied');
      });
  };

  useEffect(() => {
    let cancelled = false;

    setAccessStatus('checking');

    if (!hasAuthenticatedUser) {
      if (isPublicRoute) {
        setAccessStatus('granted');
      } else if (!isLoading) {
        logout();
      }
      return () => {
        cancelled = true;
      };
    }

    const buildType = routeProps?.match?.params?.buildType;
    const isAuthorBuildType = Boolean(route.requiredPermissionByBuildType && buildType === 'author');

    const requiredPermissions = getRequiredPermissions(route, isAuthorBuildType);
    if (requiredPermissions.length === 0) {
      setAccessStatus('granted');
    } else {
      const nonAuthorPermissions = requiredPermissions.filter(permission => permission !== permissions.author);
      const includesAuthorPermission = requiredPermissions.includes(permissions.author);

      if (hasAnyPermission(userPermissions, nonAuthorPermissions)) {
        setAccessStatus('granted');
      } else if (includesAuthorPermission) {
        evaluateAuthorCatalogAccess(() => cancelled);
      } else {
        setAccessStatus('denied');
      }
    }

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    route,
    isPublicRoute,
    isLoading,
    routeProps?.match?.params?.buildType,
    hasAuthenticatedUser,
    userPermissions,
    userAuthorCatalogs,
  ]);

  if (!isPublicRoute && (!hasAuthenticatedUser || isLoading)) {
    return React.createElement(LoadingState);
  }

  if (accessStatus === 'granted') {
    return children;
  } else if (accessStatus === 'denied') {
    return React.createElement(AccessDenied);
  } else {
    return null;
  }
}
